import { useContext, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AdGlobalRefreshContext } from 'contexts/AdGlobalRefreshContext';
import {
  transformSrpFilterStateToApiParams,
  transformSrpFilterApiParamsToState,
} from 'lib/srpFilterTransformers';
import eventLogger from 'lib/EventLogger';
import { SeoSrpPageViewEvent } from 'lib/Tracking/Srp/PageViewEvent';
import {
  SearchParamsV2,
  SearchV2AdInfo,
  VehicleV2,
  searchVehicles as baseSearchVehicles,
} from 'lib/AutolistAPI';

import { SeoSrpPageProps } from 'pages/[parentPage]';

import { SearchUrlTypes } from 'types/Apis/SearchURL';
import filterSearchParams from 'lib/filterSearchParams';
import useDeviceScreen from './useDeviceScreen';
import useHashLocation from './useHashLocation';
import { SearchVehicles } from './useSearchVehicles';
import { useSrpFilters, srpFilterInitialValues } from './useSrpFilters';
import useSrpMobileSidebar from './useSrpMobileSidebar';
import { InitialSrpParams, SrpFilterApiParams } from './useSrpModel';
import { useTrackedEffect } from './useTrackedEffect';

export interface UseSeoSrpModelResult extends SeoSrpPageProps {
  title?: string;
  url?: string;
  description?: string;
  bannerTitle?: string;
  searchParams?: Record<string, unknown>;
  isLoading: boolean;
  searchVehicles: () => Promise<void>;
  vehicles: VehicleV2[];
  totalCount: number;
  locationDisplayName?: string;
  adInfo?: SearchV2AdInfo;
}

export const useGetSearchResults = (
  ssrProps: SeoSrpPageProps
): {
  searchVehicles: SearchVehicles;
  searchResults: SeoSrpPageProps | null;
  isLoading: boolean;
  adInfo: SearchV2AdInfo | null;
} => {
  const { searchUrlAttributes } = ssrProps;
  const { search_params: searchParams = {} as SearchParamsV2 } = searchUrlAttributes!;

  const [adInfo, setAdTargetingInfo] = useState<SearchV2AdInfo | null>(null);
  const [searchResults, setSearchResults] = useState<SeoSrpPageProps | null>(ssrProps);
  const [isLoading, setIsLoading] = useState(false);

  const searchVehicles = useCallback(async (params: SrpFilterApiParams) => {
    try {
      setIsLoading(true);
      const d = await baseSearchVehicles({
        locale: searchParams.locale,
        ...params,
      });
      setAdTargetingInfo(d.adInfo);
      setSearchResults({ ...d, statusCode: 200 });
      setIsLoading(false);
    } catch (err) {
      setSearchResults({ statusCode: 500 });
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { searchVehicles, searchResults, isLoading, adInfo };
};

function filterEmptyNullParams(params: InitialSrpParams | SrpFilterApiParams): SrpFilterApiParams {
  return Object.fromEntries(
    Object.entries(params)
      .sort()
      .filter(([, v]) => !(v === null || v === undefined || (Array.isArray(v) && !v.length)))
  );
}

export default function useSeoSrpModel(ssrProps: SeoSrpPageProps): UseSeoSrpModelResult {
  const { i18n } = useTranslation();

  const { searchUrlAttributes, latitude, longitude } = ssrProps;
  const {
    title,
    title_es: titleEs,
    meta_description: description,
    meta_description_es: descriptionEs,
    search_params: searchParams = {},
    type,
    h1_tag: h1Tag,
    h1_tag_es: h1TagEs,
    make_name: makeName,
    model_name: modelName,
    year,
    location_display_name: locationDisplayName,
  } = searchUrlAttributes!;

  const { values, setValues } = useSrpFilters();
  const apiParams = transformSrpFilterStateToApiParams(values);
  const hashLocation = useHashLocation();
  const { isMobile } = useDeviceScreen();
  const { isOpen: isMobileSidebarOpen } = useSrpMobileSidebar();
  const { refreshGlobalAds } = useContext(AdGlobalRefreshContext);

  // use stringified values to avoid triggering effect when values change by-reference-only
  const apiParamsStr = JSON.stringify(apiParams);
  const hashLocationStr = JSON.stringify(hashLocation.values);

  const shouldUpdateResultsRef = useRef(false);

  const { searchVehicles, searchResults, isLoading, adInfo } = useGetSearchResults(ssrProps);

  function fetchVehicles(params: SrpFilterApiParams | undefined): void {
    searchVehicles(
      filterSearchParams({
        latitude,
        longitude,
        ...params,
        ...(isMobile && isMobileSidebarOpen && { limit: 0 }),
      })
    );
  }

  useTrackedEffect(
    (
      [apiParamsStrChanged, hashLocationStrChanged],
      initialRender,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [prevApiParamsStr, prevHashLocationStr]
    ) => {
      const shouldUpdateResults = shouldUpdateResultsRef.current;

      // Skip refetching when user navigates back to SEO SRP from VDP
      if (!shouldUpdateResults && hashLocationStrChanged) {
        if (hashLocation.values.vin) return;
        shouldUpdateResultsRef.current = true;
        // Update url with either default params on initial srp load or filter changes
        // dont run on vdp url
      } else if (initialRender && !hashLocation.values.vin) {
        // compare server search params to hash values for filter change and refresh scenario
        const serverSearchParams = searchParams as any;
        const isMatchingParams = Object.keys(hashLocation.values).every((key) => {
          return key in serverSearchParams
            ? serverSearchParams[key].toString() === hashLocation.values[key].toString()
            : false;
        });

        if (isMatchingParams) {
          hashLocation.navigate({ ...apiParams, ...hashLocation.values });
        } else {
          fetchVehicles(hashLocation.values);
          setValues(transformSrpFilterApiParamsToState(hashLocation.values));
        }

        shouldUpdateResultsRef.current = true;
        refreshGlobalAds();
        eventLogger.log(SeoSrpPageViewEvent());
      } else if (apiParamsStrChanged) {
        hashLocation.navigate({ ...hashLocation.values, ...apiParams });
        refreshGlobalAds();
        eventLogger.log(SeoSrpPageViewEvent());
        // Fetch results when url changes
      } else if (hashLocationStrChanged) {
        if (hashLocation.values.vin) {
          // Reset shouldUpdateResultsRef when navigating back to SEO SRP from VDP
          shouldUpdateResultsRef.current = false;
          return;
        }
        // Check for differences between API params and hash values
        // This is done to avoid duplicate setValues dispatches when filter values are changed
        const filteredApiParams = filterEmptyNullParams({ ...apiParams });
        let matchingParams = true;
        const mappedValues =
          Object.keys(hashLocation.values).length > Object.keys(filteredApiParams).length
            ? hashLocation.values
            : filteredApiParams;
        const checkedValues =
          Object.keys(hashLocation.values).length < Object.keys(filteredApiParams).length
            ? hashLocation.values
            : filteredApiParams;

        // Longest object is identified and iterated through to ensure all state fields are checked
        Object.keys(mappedValues).forEach((key) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const mappedValue = mappedValues[key];
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const checkedValue = checkedValues[key];
          if (!checkedValue || checkedValue.toString() !== mappedValue.toString()) {
            matchingParams = false;
          }
        });
        if (!matchingParams) {
          setValues({
            ...srpFilterInitialValues,
            ...transformSrpFilterApiParamsToState(hashLocation.values),
          });
        }

        // ensure fetching only happens after the first hash construction
        if (prevHashLocationStr !== '{}') {
          fetchVehicles(hashLocation.values);
        }
      }
    },
    [apiParamsStr, hashLocationStr]
  );

  const onSearchVehicles = (): Promise<void> =>
    searchVehicles(filterSearchParams({ ...apiParams }));

  const getBannerTitle = (): string => {
    switch (type) {
      case SearchUrlTypes.MakeSearchUrl:
        return i18n.t('seoSrp.banner.h1Tag', { ymm: makeName });
      case SearchUrlTypes.MakeModelSearchUrl:
        return i18n.t('seoSrp.banner.h1Tag', { ymm: `${makeName} ${modelName}` });
      case SearchUrlTypes.MakeModelYearSearchUrl:
        return i18n.t('seoSrp.banner.h1Tag', { ymm: `${year} ${makeName} ${modelName}` });
      case SearchUrlTypes.MakeModelLocationSearchUrl:
        return i18n.t('seoSrp.banner.makeModelLocationH1Tag', {
          mm: `${makeName} ${modelName}`,
          location: locationDisplayName,
        });
      case SearchUrlTypes.LocationSearchUrl:
        return i18n.t('seoSrp.banner.locationH1Tag', { location: locationDisplayName });
      default:
        return (i18n.language === 'en' ? h1Tag : h1TagEs) || i18n.t('seoSrp.banner.genericH1Tag');
    }
  };

  return {
    ...searchResults,
    searchVehicles: onSearchVehicles,
    isLoading,
    title: i18n.language === 'en' ? title : titleEs,
    description: i18n.language === 'en' ? description : descriptionEs,
    bannerTitle: getBannerTitle(),
    searchParams,
    locationDisplayName,
    adInfo,
  } as UseSeoSrpModelResult;
}
